import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useT } from '@/core/I18nProvider';
import { groupsForPath } from '@/i18n/catalog-groups';
import { catalogStore } from '@/i18n/catalog-store';
import { localeFromPath } from '@/i18n/locale-path';

/**
 * Keeps the i18n provider's `locale` pinned to the URL (GA-13 / ADR-0133).
 *
 * Every marketing route is published per-locale, so the URL is the source of
 * truth for the rendered language. On the initial SSR/hydration render the
 * provider is already seeded from the URL, so this is a no-op then; its job is
 * **client navigations** — following an internal link or using browser
 * back/forward to a different-locale URL must re-resolve the catalog and flip
 * `<html lang/dir>`. Renders nothing.
 */
export function LocaleSync(): null {
  const { pathname } = useLocation();
  const { locale, setLocale } = useT();

  useEffect(() => {
    const next = localeFromPath(pathname);
    if (next !== locale) {
      setLocale(next);
    }
    // Drive the catalog store for the destination document (ADR-0195): a
    // locale switch re-resolves the accumulated groups in the new locale; a
    // same-locale navigation is a piece-cached no-op (the lazy-route factories
    // already block on their own groups). The provider follows via its store
    // subscription.
    void catalogStore.ensureCatalog(next, groupsForPath(pathname));
  }, [pathname, locale, setLocale]);

  return null;
}
